import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { Heading, Stack, Text, textVariants } from '@etg/wings';
import { Notification, variants } from '@eti/components';
import { useProperty, useTranslation } from '@eti/providers';
import TopicsContentWrapper from '../TopicsContentWrapper';
import CardWithActionButton from '../../common/CardWithActionButton';
import { routes } from '../../../../../constants/routesAndApisConstants';
import FaqSection from '../../common/FaqSection';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';

interface RyanairRefundProps {
  hasScheduleChangeRefundOffer: boolean;
  isScheduleChangeOfferExpired: boolean;
}

const RyanairRefund = ({
  hasScheduleChangeRefundOffer,
  isScheduleChangeOfferExpired,
}: RyanairRefundProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { faqSectionPerSlug } = useOrderDetails();
  const faqSectionSlug = p('ContactUs.Refund.Faq.Slug');

  const leftColumnContent = useMemo(() => {
    if (hasScheduleChangeRefundOffer) {
      if (isScheduleChangeOfferExpired) {
        return {
          title: t('ManageTrip.RefundRequest.Expired.Title'),
          description: t('ContactUs.RefundRequest.Expired.Text'),
        };
      }
      return {
        title: t('ContactUs.RefundRequest.Title'),
        description: t('ContactUs.RefundRequest.Description'),
      };
    }
    return {
      title: null,
      description: t('ContactUs.Subtopic.RefundStatus.NotRequested.Content'),
    };
  }, [hasScheduleChangeRefundOffer, isScheduleChangeOfferExpired, t]);

  return (
    <TopicsContentWrapper
      chatType="oriana"
      leftColumnContent={
        <Stack spacing={24}>
          <Stack spacing={8}>
            {leftColumnContent.title && (
              <Text variant={textVariants._16_BOLD}>{leftColumnContent.title}</Text>
            )}
            <Text>{leftColumnContent.description}</Text>
          </Stack>
          {faqSectionPerSlug?.[faqSectionSlug] && (
            <FaqSection
              headingAs="h5"
              headingLevel={4}
              padding={0}
              section={faqSectionPerSlug[faqSectionSlug]}
            />
          )}
        </Stack>
      }
      rightColumnContent={
        !isScheduleChangeOfferExpired &&
        (hasScheduleChangeRefundOffer ? (
          <CardWithActionButton
            as={Link}
            buttonText={t('OrderDetails.Tab.ManageTrip.Description')}
            to={routes.ORDER_DETAILS_MANAGE_TRIP}
          >
            <Stack spacing={8}>
              <Heading as="h2" level={4}>
                {t('ManageTrip.RefundRequest.Title')}
              </Heading>
              <Text>{t('ContactUs.RefundRequest.Banner.Content')}</Text>
            </Stack>
          </CardWithActionButton>
        ) : (
          <Notification
            dataTestId="refund-status-no-contact-us-notification"
            variant={variants.WARNING}
          >
            <Text asHtml>
              {t('ContactUs.Subtopic.RefundStatus.Requested.Notification.Content')}
            </Text>
          </Notification>
        ))
      }
      shouldShowContactMethods={hasScheduleChangeRefundOffer && isScheduleChangeOfferExpired}
      snowplowProperty="REFUND"
    />
  );
};

export default RyanairRefund;
